import React, { memo, useCallback, useRef } from "react";
import { Dropdown, LocalTheme } from "@bale/ui";
import {
  DropdownList as DropdownList_,
  Item,
  ItemLabel,
  ItemText,
  ItemWrapper,
} from "./AdPanelDropdown.styles";
import { formatSelectedItem } from "./SelectedItem";
import { useTheme } from "@emotion/react";
import isEqual from "react-fast-compare";
import styled from "@emotion/styled";
import { Breakpoints, useBreakPoints } from "../../context/BreakPointContext";
import styles from "./AdPanelDropdown.module.scss";
import { Alert } from "@bale/icons";

const DropdownList = styled(DropdownList_)<{ openOnTop?: boolean }>`
  position: ${({ openOnTop }) =>
    openOnTop ? "absolute" : "relative"} !important;
`;

interface Props<T extends object> {
  items: T[];
  label?: string;
  defaultSelectedItem?: T;
  onSelectedItemChange: (item: T) => void;
  onItemClick?: (item: T) => void;
  itemToString: (item: T | null) => string;
  itemToLabel?: (item: T) => string | React.ReactNode | undefined;
  placeholderMessage: string;
  selectedItemFormatter: (item: T) => string;
  isDisable?: (item: T) => boolean;
  hasWarning?: (item: T) => boolean;
  openOnTop?: boolean;
  scrollToWhenClicked?: boolean;
  helpText?: string;
}

const CommonDropdownFC = <T extends object>(props: Props<T>) => {
  const theme = useTheme();
  const breakpoints = useBreakPoints();
  const onSelectedItemChange = useCallback(
    ({ selectedItem }: any) => {
      props.onSelectedItemChange(selectedItem);
    },
    [props.onSelectedItemChange]
  );
  const listRef = useRef<HTMLDivElement>(null);

  const onDropdownClick = useCallback(() => {
    if (props.scrollToWhenClicked) {
      setTimeout(() => {
        if (breakpoints < Breakpoints.Medium) {
          listRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      }, 0);
    }
  }, [props.scrollToWhenClicked, breakpoints]);

  return (
    <div className={styles.dropDown}>
      <Dropdown
        items={props.items}
        label={props.label}
        onSelectedItemChange={onSelectedItemChange}
        itemToString={props.itemToString}
        defaultSelectedItem={props.defaultSelectedItem}
        placeholderMessage={props.placeholderMessage}
        className={styles.baseDropDown}
        selectedItem={(selectedItemProps) =>
          formatSelectedItem(selectedItemProps, props.selectedItemFormatter)
        }
        onDropDownClick={onDropdownClick}
      >
        {({ getItemProps, selectedItem, highlightedIndex }) => (
          <DropdownList openOnTop={true} ref={listRef}>
            {props.items.map((item, index) => {
              const isDisabled = props.isDisable?.(item);
              const hasWarning = props.hasWarning?.(item);
              const itemProps = getItemProps({ item, index });

              return (
                <ItemWrapper
                  key={index}
                  {...itemProps}
                  onClick={(event) => {
                    if (!isDisabled) {
                      props.onItemClick?.(item);
                      itemProps.onClick?.(event);
                    }
                  }}
                  disabled={isDisabled}
                  isHighlighted={highlightedIndex === index}
                  theme={theme}
                  isSelected={selectedItem != null && selectedItem === item}
                >
                  <Item
                    disabled={isDisabled}
                    hasWarning={hasWarning}
                    theme={theme as LocalTheme}
                  >
                    <ItemText isPreview={false} disabled={isDisabled}>
                      {props.itemToString(item)}
                    </ItemText>
                    {props.itemToLabel && (
                      <ItemLabel>{props.itemToLabel(item)}</ItemLabel>
                    )}
                  </Item>
                </ItemWrapper>
              );
            })}
          </DropdownList>
        )}
      </Dropdown>
      {props.helpText && (
        <div className={styles.helpContainer}>
          <Alert size={16} />
          <p>{props.helpText}</p>
        </div>
      )}
    </div>
  );
};

export const AdPanelDropdown = memo(
  CommonDropdownFC,
  isEqual
) as typeof CommonDropdownFC;
